/**
 * The membership client this site uses: ./membership.js wired to the real browser.
 *
 * One instance per document, built on first use, so nothing here runs -- no storage read, no
 * client, no request -- on a page that never joins, leaves or has a leg due. See
 * docs/subsystems/loyalty-withdrawal.md → *The membership journal*.
 *
 * The two tokens are the two audiences: the WordPress routes take the fan's ID token (ours,
 * D-068), and every 3Halves call inside the leg takes the access token, through the loyalty client
 * and ../withdrawal.js.
 */

import { authReady, authState, idToken } from '../urcAuth.js';
import { getClient, invalidateFanReads } from '../client.js';
import {
	applyLoyaltyConsentAnswer,
	isLoyaltyConsentGranted,
	readLoyaltyConsentState,
} from '../consent';
import { describeLoyaltyError } from '../errorDetail';
import { forgetEnrolled } from '../toasts/enrolled';
import { createBrowserWithdrawal, isSuppressed, requestIdOf } from '../withdrawal.js';
import { createMembershipTransport } from './transport.js';
import { createMarker } from './marker.js';
import { createLegLock } from './lock.js';
import { createProcessorLeg } from './processorLeg.js';
import { createMembership } from './membership.js';

let live = null;

/**
 * @return {Storage|null} localStorage, or null where the browser refuses it.
 */
function localStore() {
	try {
		return typeof window !== 'undefined' ? window.localStorage : null;
	} catch ( error ) {
		return null;
	}
}

/**
 * @return {string} A v4 UUID.
 */
function uuid() {
	if ( typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ) {
		return crypto.randomUUID();
	}

	// Every engine this bundle targets has randomUUID; this exists so an old one still gets an id
	// the server's UUID check accepts rather than a hard failure.
	const bytes = new Uint8Array( 16 );

	crypto.getRandomValues( bytes );

	const hex = Array.from( bytes, byte => byte.toString( 16 ).padStart( 2, '0' ) ).join( '' );
	const variant = '89ab'[ bytes[ 8 ] % 4 ];

	return [
		hex.slice( 0, 8 ),
		hex.slice( 8, 12 ),
		`4${ hex.slice( 13, 16 ) }`,
		`${ variant }${ hex.slice( 17, 20 ) }`,
		hex.slice( 20, 32 ),
	].join( '-' );
}

/**
 * @param {string} message What happened.
 * @param {*}      detail  Context.
 * @return {void}
 */
function warn( message, detail ) {
	if ( typeof console !== 'undefined' && console.warn ) {
		console.warn(
			`[loyalty] ${ message }`,
			detail instanceof Error ? describeLoyaltyError( detail ) : detail
		);
	}
}

/**
 * @return {Object} The document's membership client.
 */
export function membership() {
	if ( live ) {
		return live;
	}

	const consent = ( typeof window !== 'undefined' && window.urcLoyalty?.consent ) || {};
	const storage = localStore;

	live = createMembership( {
		transport: createMembershipTransport( {
			beginUrl: consent.membershipUrl || '',
			advanceUrl: consent.advanceUrl || '',
			getCredential: idToken,
			transport: ( url, options ) => globalThis.fetch( url, options ),
		} ),
		marker: createMarker( { getStorage: storage } ),
		lock: createLegLock( {
			locks: typeof navigator !== 'undefined' && navigator.locks ? navigator.locks : null,
			getStorage: storage,
			now: () => Date.now(),
			owner: uuid(),
		} ),
		leg: createProcessorLeg( {
			// The consent-gated client: a join leg's status read goes through the gate like every
			// other read, which is why the begin's answer is handed to the consent machine first.
			// Resolved per call rather than held: ../client.js rebuilds it on an account switch,
			// and a leg must never run on a client built for somebody else.
			client: {
				getEnrolmentStatus: ( ...args ) => getClient().getEnrolmentStatus( ...args ),
				joinLoyalty: ( ...args ) => getClient().joinLoyalty( ...args ),
			},
			withdrawal: createBrowserWithdrawal(),
			requestIdOf,
			isSuppressed,
			warn,
		} ),
		identity: () => {
			const state = authState();

			return state && state.sub ? state.sub : '';
		},
		consentGranted: () => readLoyaltyConsentState().granted === true,
		ensureConsentRead: () => isLoyaltyConsentGranted(),
		onAnswer: applyLoyaltyConsentAnswer,
		onSettled: sub => {
			forgetEnrolled( sub );
			invalidateFanReads();
		},
		now: () => Date.now(),
		uuid,
		eraseData: consent.eraseData === true,
		warn,
	} );

	return live;
}

/**
 * Resume a membership leg left due by an earlier page, once the fan is known. Never rejects, and
 * costs nothing -- not even a storage read past the marker -- when there is nothing due.
 *
 * @return {void}
 */
export function resumeMembership() {
	authReady()
		.then( state => ( state && state.sub ? membership().resume() : null ) )
		.catch( error => warn( 'resuming a membership leg failed', error ) );
}
